import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import produce from 'immer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import "./ApproveModal.scss";
import { Alert, ButtonCountdown, ErrorBoundary, ErrorMessage, Loader, Stepper } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import { AffectedDevicesWarning, AsyncStatus, FilterType, UserRole } from 'enums';
import { IDevice, IDeviceModel, IUpdateFile } from 'interfaces';
import { ManufacturersService, parseAxiosError, UpdateFilesService, UpdateTypesService, UsersService } from 'services';
import { ICheck, UpdatePageModel } from '../UpdatePageModel';
import { useLuxonTimezone } from 'hooks';
import { DateTime } from 'luxon';
import { formatDate, formatDurationShort, formatTime } from 'services/Utils';

export enum ApproveModalMode {
    SubmitForApproval = 'SubmitForApproval',
    Approve = 'Approve',
}

enum ScheduleStageIconType {
    Success = 'Success',
    Warn = 'Warn',
    Error = 'Error',
}

interface IApproveModalProps {
    mode: ApproveModalMode,
    model: UpdatePageModel,
    show: boolean,
    onClose: () => void,
}

interface IApproveModalState {
    acknowledgeBigUpdate?: boolean,
    acknowledgeStartSoon?: boolean,
    filterDevicesByThingID: {[thingId: string]: IDevice},
    loadError?: string,
    loadStatus: AsyncStatus,
    saveStatus: AsyncStatus,
    showFilterCounts?: boolean,
    showScheduleStageChecks: {[stageId: number]: boolean},
    step: number,
    updateFile?: IUpdateFile,
    isFinished: boolean,
}

const CheckItem = function CheckItem({ check }: {check: ICheck}) {
    return (
        <div className={cn('approveModal_check', {
            warn: !check.isPassing && !check.isRequired,
            error: !check.isPassing && check.isRequired,
        })}>
            <div className="approveModal_checkIconWrap">
                {check.isPassing && <FontAwesomeIcon icon="check" className="approveModal_checkIcon success" />}
                {!check.isPassing && (<>
                    {check.isRequired && <FontAwesomeIcon icon="times" className="approveModal_checkIcon error" />}
                    {!check.isRequired && <FontAwesomeIcon icon="exclamation-triangle" className="approveModal_checkIcon warn" />}
                </>)}
            </div>
            <div className="approveModal_checkLabel">
                {check.message}
            </div>
        </div>
    );
}

export function ApproveModal({ mode, model, show, onClose }: IApproveModalProps) {
    // Prevent errors while the page is loading. This doesn't break hooks because we recreate the modal on open.
    if (model.loadStatus !== AsyncStatus.Success) return null;
    
    const { update } = model;
    const { t } = useTranslation();
    const { hasTimezone, timezone } = useLuxonTimezone(UsersService.me.timezone);
    const isSubmit = (mode === ApproveModalMode.SubmitForApproval);
    const maxSteps = 4;
    const [state, setState] = useState<IApproveModalState>(() => {
        // On the Schedule step, start a stage open if it has failing checks
        const showScheduleStageChecks = {};
        for (const s of update.rollout.stages) {
            const checks = model.scheduleChecks[s.id]
            if (checks) {
                for (const check of checks) {
                    if (!check.isPassing) {
                        showScheduleStageChecks[s.id] = true;
                        break;
                    }
                }
            }
        }

        return {
            filterDevicesByThingID: {},
            loadStatus: AsyncStatus.Initial,
            saveStatus: AsyncStatus.Initial,
            showScheduleStageChecks: showScheduleStageChecks,
            step: 1,
            isFinished: false,
        };
    });
    const {
        acknowledgeBigUpdate,
        acknowledgeStartSoon,
        filterDevicesByThingID,
        loadError,
        loadStatus,
        saveStatus,
        showFilterCounts,
        showScheduleStageChecks,
        step,
        updateFile,
    } = state;
    const isSaving = saveStatus === AsyncStatus.Loading;

    let scheduleStageIcons: {[stageId: number]: ScheduleStageIconType} = {};
    for (const s of update.rollout.stages) {
        let hasWarn = false;
        let hasError = false;
        if (model.scheduleChecks[s.id]) {
            for (const check of model.scheduleChecks[s.id]) {
                if (!check.isPassing) {
                    if (check.isRequired) hasError = true;
                    else hasWarn = true;
                }
            }
        }

        if (hasError) scheduleStageIcons[s.id] = ScheduleStageIconType.Error;
        else if (hasWarn) scheduleStageIcons[s.id] = ScheduleStageIconType.Warn;
        else scheduleStageIcons[s.id] = ScheduleStageIconType.Success;
    }
    

    // Load the updateFile so we have the list of all possible models for this update.
    useEffect(() => {
        if (show && loadStatus === AsyncStatus.Initial) {
            if (!update.updateTypeId) {
                setState(produce(draft => {
                    draft.loadStatus = AsyncStatus.Error;
                    draft.loadError = 'Please select an update version before submitting.';
                }));
                return;
            }

            const uf = UpdateFilesService.allUpdateFiles
                .find(uf => uf.id === model.update.updateFileId)

            if (!uf) {
                setState(produce(draft => {
                    draft.loadStatus = AsyncStatus.Error;
                    draft.loadError = `Couldn't find updateFile for id: ` + model.update.updateFileId;
                }))
                return;
            }

            setState(produce(draft => {
                draft.loadStatus = AsyncStatus.Success;
                draft.loadError = undefined;
                draft.updateFile = uf;
            }))
        }
    }, [show, loadStatus]);

    let allModels: IDeviceModel[] = [];
    if (updateFile && updateFile.deviceModelIds) {
        allModels = updateFile.deviceModelIds.map(id => ManufacturersService.modelsMap[id]);
    }

    const selectedModels: {[modelId: number]: boolean} = {};
    if (update.deviceModelIds) {
        for (const id of update.deviceModelIds) {
            selectedModels[id] = true;
        }
    }

    const deviceChecks: ICheck[] = [];
    // Check to make sure limited users can't deploy to more than 3 devices.
    if (mode === ApproveModalMode.SubmitForApproval) {
        if (UsersService.oktaUserInfo.role < UserRole.FullUser) {
            deviceChecks.push({
                isPassing: model.affected?.combined.devicesCount <= 3,
                isRequired: true,
                message: `Your role can only deploy to up to 3 devices.`,
            });
        } else {
            deviceChecks.push({
                isPassing: true,
                isRequired: true,
                message: `Your role can deploy to an unlimited number of devices.`,
            });
        }
    }

    const affectedCombinedErr = model.affected?.combined.err;
    let filtersCheckMessage = 'All filters are valid.'
    let filtersCheckPass = false;
    if (model.affectedStatus === AsyncStatus.Success && !model.affected?.combined.err) {
        filtersCheckPass = true;
    } else if (model.affectedStatus !== AsyncStatus.Success) {
        filtersCheckMessage = 'Error checking filters: ' + model.affectedError;
    } else {
        filtersCheckMessage = 'Filters validation error: ' + model.affected?.combined.err;
    }

    deviceChecks.push({
        isPassing: filtersCheckPass,
        isRequired: true,
        message: filtersCheckMessage,
    });

    let hasGlobalLocationsFilter = !!model.update?.rollout.global.filters.find(f => f.type === FilterType.Locations);
    let everyStageHasLocationsFilter = model.update?.rollout.stages.every(stage => {
        return !!stage.filters.find(f => f.type === FilterType.Locations);
    })

    deviceChecks.push({
        isPassing: hasGlobalLocationsFilter || everyStageHasLocationsFilter,
        isRequired: true,
        message: `A "Locations" filter is in every stage or in global filters.`,
    });

    const stageUpdateAllInGlobal = !model.affected?.combined.warnings?.find(w => {
        return w.type === AffectedDevicesWarning.StagesDontUpdateAll;
    });
    deviceChecks.push({
        isPassing: stageUpdateAllInGlobal,
        isRequired: false,
        message: `If global filters are present, warn if stages don't update all devices targeted by global filters.`,
    });

    const dupeSerialsWarning = model.affected?.combined.warnings?.find(w => {
        return w.type === AffectedDevicesWarning.DuplicateSerials;
    });
    deviceChecks.push({
        isPassing: !dupeSerialsWarning,
        isRequired: false,
        message: dupeSerialsWarning
            ? 'Duplicate serial numbers detected.'
            : 'No duplicate serial numbers detected.',
    });

    let promptStartSoon = false;
    const firstStageStartDt = DateTime.fromJSDate(update.rollout.stages[0].startTime);
    if (firstStageStartDt <= DateTime.now().plus({days: 1})) {
        promptStartSoon = true;
    }

    // TODO - wire up promptBigUpdate when we do affectedDevices
    let promptBigUpdate = false;

    const isStepValid: {[step: number]: boolean} = {
        1: true,
        2: areChecksValid(deviceChecks),
        // Make sure all checks for all stages are valid
        3: update.rollout.stages.every(stage => {
            const checks = model.scheduleChecks[stage.id];
            return checks && areChecksValid(checks);
        }),
        4: (!promptStartSoon || acknowledgeStartSoon) && (!promptBigUpdate || acknowledgeBigUpdate),
    }

    let nextEnabled = (
        !isSaving
        && loadStatus === AsyncStatus.Success
        && saveStatus !== AsyncStatus.Success
        && step < maxSteps
        && isStepValid[step]
    );
    let prevEnabled = (
        !isSaving 
        && loadStatus === AsyncStatus.Success
        && saveStatus !== AsyncStatus.Success
        && step > 1
    );

    function areChecksValid(checks: ICheck[]): boolean {
        for (const c of checks) {
            if (c.isRequired && !c.isPassing) {
                return false;
            }
        }
        return true;
    }

    function onToggleShowFilterCounts() {
        setState(produce(draft => {
            draft.showFilterCounts = !draft.showFilterCounts;
        }))
    }

    function onToggleScheduleChecks(stageId: number) {
        setState(produce(draft => {
            draft.showScheduleStageChecks[stageId] = !draft.showScheduleStageChecks[stageId];
        }))
    }

    function onNext() {
        if (nextEnabled) {
            setState(produce(draft => {
                draft.step = step + 1;
            }))
        }
    }

    function onPrev() {
        if (prevEnabled) {
            setState(produce(draft => {
                draft.step = step - 1;
            }))
        }
    }

    function onTryCancel() {
        // If we're in the middle of a save call, don't close
        if (isSaving) return;
        onClose();
    }

    function onToggleAckStartSoon() {
        setState(produce(draft => {
            draft.acknowledgeStartSoon = !draft.acknowledgeStartSoon;
        }))
    }

    function onSubmitForApproval() {
        doAPICall(() => model.submitForApproval());
    }

    function onApprove() {
        doAPICall(() => model.approve());
    }

    function doAPICall(call: () => Promise<any>) {

        setState(produce(draft => {
            draft.saveStatus = AsyncStatus.Loading;
        }));

        call()
            .then(() => {
                setState(produce(draft => {
                    draft.isFinished = true;
                    draft.saveStatus = AsyncStatus.Success;
                }));
            })
            .catch(() => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Error;
                }));
            });
    }

    return (
        <Modal show={show} onClose={onTryCancel} className="approveModal" size="lg" scrollMode='modal-body'>
            <ErrorBoundary>
                <ModalHeader>
                    <div className="approveModal_headerWrap">
                        <div className="approveModal_title">
                            {isSubmit ? 'Confirm Update Submission' : 'Approve Update'}
                        </div>
                        <Stepper activeStep={step} steps={['Properties', 'Devices and Locations', 'Schedule', 'Submit']} />
                    </div>
                </ModalHeader>
                <ModalBody style={{textAlign: "left"}}>
                    {(loadStatus === AsyncStatus.Initial || loadStatus === AsyncStatus.Loading) && (
                        <Loader />
                    )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                    {loadStatus === AsyncStatus.Success && (<>
                        {step === 1 && (<>
                            {mode === ApproveModalMode.Approve && (<>
                                <hr />
                                <p>
                                    Please carefully review this update and make sure it looks correct.
                                    If changes need to be made, please contact the submitting user.
                                </p>
                                <hr />
                                <div className="approveModal_fieldLabel">
                                    Submitted by
                                </div>
                                <div className="approveModal_submittedBy">
                                    <FontAwesomeIcon icon="user" className="approveModal_submittedByIcon" />
                                    {update?.submittedBy?.name} ({update?.submittedBy?.email || 'No email'})
                                </div>
                                <hr />
                            </>)}

                            <div className="approveModal_updateFileInfo">
                                <div className="approveModal_updateFileCol">
                                    <div className="formItem">
                                        <div className="formItem_label">
                                            Manufacturer
                                        </div>
                                        <div className="approveModal_fakeInput">
                                            {ManufacturersService.manufacturersMap[update?.manufacturerId]?.name}
                                        </div>
                                    </div>
                                </div>
                                <div className="approveModal_updateFileCol">
                                    <div className="formItem">
                                        <div className="formItem_label">
                                            Update Type
                                        </div>
                                        <div className="approveModal_fakeInput">
                                            {UpdateTypesService.updateTypesMap[update?.updateTypeId]?.description}
                                        </div>
                                    </div>
                                </div>
                                <div className="approveModal_updateFileCol">
                                    <div className="formItem">
                                        <div className="formItem_label">
                                            Update Version
                                        </div>
                                        <div className="approveModal_fakeInput">
                                            <div>{updateFile?.version}</div>
                                            <div>{updateFile?.description}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="formItem">
                                <div className="formItem_label">
                                    Targeted Device Models
                                </div>
                                <ul className="approveModal_models">
                                    {allModels.map((m, i) => (
                                        <li className="approveModal_model" key={m.id}>
                                            <label>
                                                {/* For "checked", we have to cast to boolean to make sure the property is still passed if false */}
                                                <input
                                                    type="checkbox"
                                                    checked={!!selectedModels[m.id]}
                                                    disabled={true}
                                                />
                                                {m.name}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>)}
                        {step === 2 && (<>
                            <p style={{"marginTop": "0"}}>
                                This update will currently affect:
                            </p>
                            <table className="approveModal_countsTable">
                                <thead>
                                    <tr>
                                        <th className="approveModal_countsTable_colLabel"></th>
                                        <th className="approveModal_countsTable_colDevices">Devices</th>
                                        <th className="approveModal_countsTable_colLocations">Locations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="approveModal_countsTableStageRow">
                                        <td className="approveModal_countsTableLabel">
                                            Global filters
                                        </td>
                                        <td className="approveModal_countsTableNumber">
                                            {model.affected?.rollout.global.result.devicesCount || 0}
                                        </td>
                                        <td className="approveModal_countsTableNumber">
                                            {model.affected?.rollout.global.result.locationsCount || 0}
                                        </td>
                                    </tr>
                                    {update.rollout.global.filters.map((filter, i) => (
                                        <tr className="approveModal_countsTableFilterRow" key={`globalFilter_${i}`}>
                                            <td className="approveModal_countsTableLabel">
                                                {t('filterType.' + filter.type)} Filter
                                            </td>
                                            <td className="approveModal_countsTableNumber">
                                                {showFilterCounts && (model.affectedByFilterId[filter.id]?.devicesCount || 0)}
                                            </td>
                                            <td className="approveModal_countsTableNumber">
                                                {showFilterCounts && (model.affectedByFilterId[filter.id]?.locationsCount || 0)}
                                            </td>
                                        </tr>
                                    ))}
                                    {update.rollout.stages.map((stage, i) => {
                                        const err = model.affectedByStageId[stage.id]?.err;
                                        
                                        return <React.Fragment key={'stage_' + i}>
                                            <tr className={cn("approveModal_countsTableStageRow", {
                                                error: !!err,
                                            })}>
                                                <td className="approveModal_countsTableLabel">
                                                    {err && (<>
                                                        <FontAwesomeIcon icon="times" />&nbsp;
                                                    </>)}
                                                    Stage {i + 1}
                                                </td>
                                                <td className="approveModal_countsTableNumber">
                                                    {model.affectedByStageId[stage.id]?.devicesCount || 0}
                                                </td>
                                                <td className="approveModal_countsTableNumber">
                                                    {model.affectedByStageId[stage.id]?.locationsCount || 0}
                                                </td>
                                            </tr>
                                            {stage.filters.map((filter, j) => {
                                                const err = model.affectedByFilterId[filter.id]?.err;

                                                return (
                                                    <tr 
                                                        className={cn("approveModal_countsTableFilterRow", {
                                                            error: !!err,
                                                        })}
                                                        key={`stage_${i}_filter_${j}`}
                                                    >
                                                        <td className="approveModal_countsTableLabel">
                                                            {err && (<>
                                                                <FontAwesomeIcon icon="times" />&nbsp;
                                                            </>)}
                                                            {t('filterType.' + filter.type)} Filter
                                                        </td>
                                                        <td className="approveModal_countsTableNumber">
                                                            {showFilterCounts && (model.affectedByFilterId[filter.id]?.devicesCount || 0)}
                                                        </td>
                                                        <td className="approveModal_countsTableNumber">
                                                            {showFilterCounts && (model.affectedByFilterId[filter.id]?.locationsCount || 0)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {stage.filters.length === 0 && (
                                                <tr className="approveModal_countsTableFilterRow">
                                                    <td className="approveModal_countsTableLabel">
                                                        {'<'}No filters{'>'}
                                                    </td>
                                                    <td className="approveModal_countsTableNumber"></td>
                                                    <td className="approveModal_countsTableNumber"></td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    })}
                                    <tr className="approveModal_countsTableTotalsRow">
                                        <td className="approveModal_countsTableLabel">
                                            Total unique
                                        </td>
                                        <td className="approveModal_countsTableNumber">
                                            {model.affected?.combined.devicesCount || 0}
                                        </td>
                                        <td className="approveModal_countsTableNumber">
                                            {model.affected?.combined.locationsCount || 0}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <p>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={!!showFilterCounts}
                                        onChange={onToggleShowFilterCounts}
                                    />
                                    Show filter counts
                                </label>
                            </p>
                            <hr />
                            <div className="approveModal_fieldLabel">
                                Checks
                            </div>
                            <div className="approveModal_checks">
                                {deviceChecks.map((check, i) => (
                                    <CheckItem check={check} key={'check_' + i} />
                                ))}
                            </div>
                            {dupeSerialsWarning && (<>
                                <Alert type="warn">
                                    <FontAwesomeIcon icon="exclamation-triangle" />&nbsp;
                                    Warning: detected multiple targeted devices with the same serial number:
                                </Alert>
                                <div className="approveModal_duplicateSerials">
                                    {dupeSerialsWarning.deviceDuplicates.map((dupeItem, i) => (
                                        <div key={i}>
                                            <div className="approveModal_duplicateSerialsItem">
                                                {dupeItem.serialNumber || '<No serial number>'} - {dupeItem.thingIDs.length} devices:
                                            </div>
                                            <div className="approveModal_duplicateSerialsDevices">
                                                {dupeItem.thingIDs.map((thingId, j) => (
                                                    <div key={j}>
                                                        <span className="approveModal_duplicateSerial_thingId">
                                                            ThingID: {thingId || '<Blank>'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>)}
                        </>)}
                        {step === 3 && (<>
                            <div className="approveModal_scheduleStageHeaders">
                                <div className="approveModal_scheduleStageHeaderIconSpace"></div>
                                <div className="approveModal_scheduleStageHeader">
                                    Stage
                                </div>
                                <div className="approveModal_scheduleStageHeader">
                                    Start
                                </div>
                                <div className="approveModal_scheduleStageHeader">
                                    Duration
                                </div>
                                <div className="approveModal_scheduleStageHeader">
                                    End
                                </div>
                                <div className="approveModal_scheduleStageHeaderButtonSpace"></div>
                            </div>
                            {update.rollout.stages.map((stage, i) => (
                                <React.Fragment key={'stage_' + i}>
                                    {i !== 0 && stage.requiresConfirmation && (
                                        <div className="approveModal_scheduleStageConfirm">
                                            <div className="approveModal_scheduleStageConfirmLine"></div>
                                            <div className="approveModal_scheduleStageConfirmIcon">
                                                <FontAwesomeIcon icon="shield-alt" /> User confirms Stage {i + 1}
                                            </div>
                                            <div className="approveModal_scheduleStageConfirmLine"></div>
                                        </div>
                                    )}
                                    <div
                                        className={cn('approveModal_scheduleStage', {
                                            success: scheduleStageIcons[stage.id] === ScheduleStageIconType.Success,
                                            warn: scheduleStageIcons[stage.id] === ScheduleStageIconType.Warn,
                                            error: scheduleStageIcons[stage.id] === ScheduleStageIconType.Error,
                                        })}
                                        onClick={() => onToggleScheduleChecks(stage.id)}
                                    >
                                        <div className="approveModal_scheduleStageToggle">
                                            {scheduleStageIcons[stage.id] === ScheduleStageIconType.Success && (
                                                <FontAwesomeIcon icon="check" className="approveModal_checkIcon success" />
                                            )}
                                            {scheduleStageIcons[stage.id] === ScheduleStageIconType.Warn && (
                                                <FontAwesomeIcon icon="exclamation-triangle" className="approveModal_checkIcon warn" />
                                            )}
                                            {scheduleStageIcons[stage.id] === ScheduleStageIconType.Error && (
                                                <FontAwesomeIcon icon="times" className="approveModal_checkIcon error" />
                                            )}
                                        </div>
                                        <div className="approveModal_scheduleStageField">
                                            Stage {i+1}
                                        </div>
                                        <div className="approveModal_scheduleStageField">
                                            <div>
                                                {formatDate(stage.startTime, timezone)}
                                            </div>
                                            <div>
                                                {formatTime(stage.startTime, timezone, hasTimezone)}
                                            </div>
                                        </div>
                                        <div className="approveModal_scheduleStageField">
                                            {formatDurationShort(stage.startTime, stage.stopTime, timezone)}
                                        </div>
                                        <div className="approveModal_scheduleStageField">
                                            <div>
                                                {formatDate(stage.stopTime, timezone)}
                                            </div>
                                            <div>
                                                {formatTime(stage.stopTime, timezone, hasTimezone)}
                                            </div>
                                        </div>
                                        <div className="approveModal_scheduleStageToggle">
                                            {!showScheduleStageChecks[stage.id] && <FontAwesomeIcon icon="chevron-right" />}
                                            {showScheduleStageChecks[stage.id] && <FontAwesomeIcon icon="chevron-down" />}
                                        </div>
                                    </div>
                                    {showScheduleStageChecks[stage.id] && (
                                        <div className="approveModal_scheduleStageChecks">
                                            {model.scheduleChecks[stage.id]?.map((check, j) => (
                                                <CheckItem check={check} key={'check_' + j} />
                                            ))}
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </>)}
                        {step === 4 && (<>
                            {/* Body text */}
                            {mode === ApproveModalMode.SubmitForApproval && (<>
                                <h2>Are you really sure you want to submit this update?</h2>
                                <ul>
                                    <li>You won't be able to edit this update after you submit it.</li>
                                    <li>It will be scheduled for deployment after being approved.</li>
                                    <li>If you need to make changes to an update after it's been submitted,
                                        you can revert it to a draft, then resubmit it.</li>
                                </ul>
                            </>)}
                            {mode === ApproveModalMode.Approve && (<>
                                <h2>Are you really sure you want to approve this update?</h2>
                                <ul>
                                    <li>It will be immediately scheduled for deployment.</li>
                                    <li>If you need to make changes to an approved update,
                                        you can still revert it to draft, as long as deployment hasn't started yet.</li>
                                </ul>
                            </>)}

                            {/* Final prompts for certain warnings */}
                            {promptStartSoon && (() => {
                                const now = new Date();
                                const immediate = update.rollout.stages[0].startTime <= now;
                                const modeApprove = mode === ApproveModalMode.Approve;

                                // Build the time string. If we ever localize the app, we'll have to change how we do this.
                                let startString = 'immediately';
                                if (!immediate) {
                                    startString = 'in ' + formatDurationShort(now, update.rollout.stages[0].startTime, timezone, {
                                        unitDisplay: 'long',
                                    });
                                }

                                return (<>
                                    <Alert type="warn">
                                        <FontAwesomeIcon icon="exclamation-triangle" />
                                        &nbsp;&nbsp;<b>Warning:</b>
                                        &nbsp;
                                        {!modeApprove && `This update will start ${startString} once approved.`}
                                        {modeApprove && `This update will start ${startString}.`}
                                        &nbsp;Please confirm this is intended.
                                    </Alert>
                                    <label>
                                        <input type="checkbox" checked={!!acknowledgeStartSoon} onChange={onToggleAckStartSoon} />
                                        &nbsp;
                                        {!modeApprove && `I acknowledge update will start ${startString} once approved.`}
                                        {modeApprove && `I acknowledge update will start ${startString}.`}
                                    </label>
                                </>)
                            })()}

                            {/* TODO - Big update warning */}

                            {/* Success and error alerts */}
                            {mode === ApproveModalMode.SubmitForApproval && (<>
                                {saveStatus === AsyncStatus.Success && (
                                    <Alert type="success">
                                        <b>Success!</b> Update was submitted.
                                    </Alert>
                                )}
                                {saveStatus === AsyncStatus.Error && (<>
                                    <Alert type="danger">
                                        <b>An error occured:</b> {model.apiCallError}
                                    </Alert>
                                </>)}
                            </>)}
                            {mode === ApproveModalMode.Approve && (<>
                                {saveStatus === AsyncStatus.Success && (
                                    <Alert type="success">
                                        <b>Success!</b> Update is scheduled for deployment.
                                    </Alert>
                                )}
                                {saveStatus === AsyncStatus.Error && (<>
                                    <Alert type="danger">
                                        <b>An error occured:</b> {model.apiCallError}
                                    </Alert>
                                </>)}
                            </>)}
                        </>)}
                        {/* Show an error if the step isn't valid. For the last step, we'll just disable the button */}
                        {!isStepValid[step] && step !== 4 && (
                            <Alert type="danger">
                                {mode === ApproveModalMode.SubmitForApproval && (<>
                                    The above errors need to be corrected before submitting this update.
                                </>)}
                                {mode === ApproveModalMode.Approve && (<>
                                    The above errors need to be corrected before this update can be approved. <br/>
                                    Please have the submitting user revert this update to a draft to make changes.
                                </>)}
                            </Alert>
                        )}
                    </>)}
                </ModalBody>
                <ModalFooter>
                    <Button variant="outlined" onClick={onTryCancel} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button 
                        style={{marginLeft: "10px"}}
                        variant="outlined" 
                        color="secondary" 
                        onClick={onPrev} 
                        disabled={!prevEnabled}
                    >
                        Back
                    </Button>
                    {step < maxSteps && (
                        <Button 
                            style={{marginLeft: "10px"}}
                            variant="outlined" 
                            color="secondary" 
                            onClick={onNext} 
                            disabled={!nextEnabled}
                        >
                            Next
                        </Button>
                    )}
                    
                    {step === maxSteps && (<>
                        {mode === ApproveModalMode.SubmitForApproval && !state.isFinished && (
                            <ButtonCountdown
                                style={{marginLeft: "10px"}}
                                onClick={onSubmitForApproval}
                                loading={saveStatus === AsyncStatus.Loading}
                                disabled={!isStepValid[4] || saveStatus === AsyncStatus.Success}
                            >
                                {isSaving && 'Submitting...'}
                                {!isSaving && 'Submit for Approval'}
                            </ButtonCountdown>
                        )}
                        {mode === ApproveModalMode.Approve && !state.isFinished && (
                            <ButtonCountdown
                                style={{marginLeft: "10px"}}
                                onClick={onApprove}
                                loading={saveStatus === AsyncStatus.Loading}
                                disabled={!isStepValid[4] || saveStatus === AsyncStatus.Success}
                            >
                                {isSaving && 'Approving...'}
                                {!isSaving && 'Approve Update'}
                            </ButtonCountdown>
                        )}
                        {state.isFinished && (
                            <Button 
                                style={{marginLeft: "10px"}}
                                onClick={onClose}
                            > 
                                Continue
                            </Button>
                        )}
                    </>)}
                </ModalFooter>
            </ErrorBoundary>
        </Modal>
    );
}